import { BaseModel } from '@/api/base-model'
import { PipelineAPI } from '@/api/backend-client'
import { Company } from '@/api/model/company'
import { PipelineCategory } from '@/api/types'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { Branch } from '@/api/model/branch'
import { User } from '@/api/model/user'
import { PipelineLinkedBranch } from '@/api/model/pipeline-linked-branch'

export class Pipeline extends BaseModel<Pipeline> {
    protected collectionName = PipelineAPI.collectionName

    constructor(
        public company: Company|null,
        public branches: Branch[],
        public linkedBranches: PipelineLinkedBranch[],
        public fallbackBranch: Branch,
        public id: string,
        public name: string,
        public category: PipelineCategory,
        public active: boolean,
        public phases: PipelinePhase[],
        public conversionPercentage: number|null,
        public afspraakTypes: string[],
        public autoCloseAfterDays: number|null,
        public createdAt: Date,
        public updatedAt: Date,
    ) { super(id) }

    isExternal(company: Company): boolean {
        return this.company && this.company.id !== company.id
    }

    get firstPhase(): PipelinePhase|null {
        if (this.phases.length === 0) {
            return null
        }

        return this.phases[0]
    }

    get activePhases(): PipelinePhase[] {
        return this.phases
            .filter(phase => phase.active)
    }

    getPhase(id: string): PipelinePhase|null {
        return this.phases
            .find(phase => phase.id === id)
    }

    canViewPotentialOrderValue(user: User): boolean {
        if (!this.linkedBranches.some(linkedBranch => linkedBranch.potentialOrderValuePerReferral !== null)) {
            return false
        }

        if (user.company === null) {
            return false
        }

        if (this.isExternal(user.company)) {
            return false
        }

        return user.company.potentialOrderValueRoles.includes(user.role)
    }

    static fromData(data: any): Pipeline {
        if (data === null || typeof data === 'string' || data instanceof Pipeline) {
            return data
        }

        return new Pipeline(
            data.company ? Company.fromData(data.company) : null,
            Object.values(data.branches || {}).map(branch => Branch.fromData(branch)),
            Object.values(data.linkedBranches || {}).map(linkedBranch => PipelineLinkedBranch.fromData(linkedBranch)),
            data.fallbackBranch ? Branch.fromData(data.fallbackBranch) : null,
            data.id,
            data.name,
            data.category,
            data.active,
            Object.values(data.phases || {}).map(entry => PipelinePhase.fromData(entry)).sort((a, b) => a.compareTo(b)),
            data.conversionPercentage,
            data.afspraakTypes || [],
            data.autoCloseAfterDays,
            data.createdAt,
            data.updatedAt,
        )
    }
}
